import React, { useEffect, useMemo, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Spinner } from '../ui.jsx';

// Live lead map — geocodes contact addresses and plots them. Uses OpenStreetMap tiles
// via Leaflet (no API key needed). Filters by recency + stage.
//
// Loads Leaflet from CDN at runtime to avoid an npm dep just for this view.

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

function loadLeaflet() {
  return new Promise((resolve, reject) => {
    if (window.L) return resolve(window.L);
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const css = document.createElement('link');
      css.rel = 'stylesheet'; css.href = LEAFLET_CSS;
      document.head.appendChild(css);
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

const STAGE_COLOR = {
  'New Lead': '#22d3ee',
  'Contacted': '#a78bfa',
  'Qualified': '#fbbf24',
  'Booked': '#22c55e',
  'Estimate Sent': '#f97316',
  'Invoiced': '#3b82f6',
  'Paid': '#10b981',
  'Lost': '#94a3b8',
};

export function LeadMap({ toast }) {
  const [contacts, setContacts] = useState(null);
  const [recency, setRecency] = useState(90); // days — wider default so older leads show by default
  const [stageFilter, setStageFilter] = useState('all');
  const [map, setMap] = useState(null);

  useEffect(() => { api('/api/contacts').then(setContacts).catch(e=>toast(e.message,'error')); }, []);

  const filtered = useMemo(() => {
    if (!contacts) return [];
    const cutoff = Date.now() - recency * 86400000;
    return contacts.filter(c => {
      if (stageFilter !== 'all' && c.stage !== stageFilter) return false;
      const created = c.createdAt ? new Date(c.createdAt).getTime() : 0;
      return created >= cutoff;
    });
  }, [contacts, recency, stageFilter]);

  useEffect(() => {
    let L, leafletMap, layer;
    let cancelled = false;
    (async () => {
      try {
        L = await loadLeaflet();
        if (cancelled) return;
        if (!document.getElementById('lead-map-root')) return;
        leafletMap = L.map('lead-map-root').setView([27.9506, -82.4572], 9); // Tampa default
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap',
          maxZoom: 19,
        }).addTo(leafletMap);
        setMap(leafletMap);

        // Geocode + render
        layer = L.layerGroup().addTo(leafletMap);
        const bounds = [];
        for (const c of filtered) {
          const addr = [c.address, c.city, c.state, c.zip].filter(Boolean).join(', ');
          if (!addr) continue;
          try {
            const r = await api(`/api/contacts/geocode?address=${encodeURIComponent(addr)}`).catch(()=>null);
            if (!r?.lat || !r?.lng) continue;
            const color = STAGE_COLOR[c.stage] || '#a78bfa';
            const pin = L.circleMarker([r.lat, r.lng], {
              radius: 9, color, fillColor: color, fillOpacity: .85, weight: 2,
            }).addTo(layer);
            pin.bindPopup(`
              <div style="font-family:system-ui,sans-serif;min-width:160px">
                <div style="font-weight:700">${c.firstName} ${c.lastName||''}</div>
                <div style="color:#888;font-size:12px">${c.stage} · ${c.service||''}</div>
                <div style="margin-top:4px"><a href="tel:${c.phone}" style="color:#22d3ee">${c.phone||''}</a></div>
                <div style="font-size:11px;color:#888;margin-top:4px">${addr}</div>
              </div>
            `);
            bounds.push([r.lat, r.lng]);
          } catch {}
          if (cancelled) return;
        }
        // Only auto-fit on the first render so the user's pan/zoom is preserved when
        // they change filters mid-session.
        if (bounds.length && !window.__leadMapFitOnce) {
          leafletMap.fitBounds(bounds, { padding: [30, 30] });
          window.__leadMapFitOnce = true;
        }
      } catch (e) {
        console.error('Map load failed:', e);
      }
    })();
    return () => {
      cancelled = true;
      if (leafletMap) leafletMap.remove();
    };
  }, [filtered.map(c => c.id).join(',')]);

  if (!contacts) return <div style={{ padding:40 }}><Spinner sz={32}/></div>;

  const stages = ['all', ...new Set(contacts.map(c => c.stage))];

  return <div style={{ padding:24, display:'flex', flexDirection:'column', gap:14, height:'calc(100vh - 64px)' }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:14 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>🗺 Live Lead Map <span style={{ color:G.textMuted, fontWeight:500, fontSize:16 }}>({filtered.length})</span></h1>
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <span style={{ fontSize:12, color:G.textMuted }}>Recency:</span>
        {[1, 7, 30, 90, 365, 3650].map(d => <Btn key={d} sz="xs" v={recency===d?'primary':'ghost'} onClick={()=>setRecency(d)}>{d>=3650?'All':d>=365?`${Math.round(d/365)}y`:`${d}d`}</Btn>)}
        <span style={{ marginLeft:14, fontSize:12, color:G.textMuted }}>Stage:</span>
        <select value={stageFilter} onChange={e=>setStageFilter(e.target.value)} style={{ padding:'6px 10px', borderRadius:6, background:G.input, color:G.text, border:`1px solid ${G.cardBorder}` }}>
          {stages.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>
    </div>

    <div style={{ display:'flex', gap:14, flex:1, minHeight:0 }}>
      <div id="lead-map-root" style={{ flex:1, borderRadius:12, overflow:'hidden', border:`1px solid ${G.border}` }}></div>
      <div style={{ width:300, overflowY:'auto', display:'flex', flexDirection:'column', gap:8 }}>
        <div style={{ fontSize:12, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', padding:'4px 2px' }}>Recent {filtered.length} leads</div>
        {filtered.slice(0, 50).map(c => <div key={c.id} style={{ ...styles.card, padding:10, cursor:'pointer' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <strong style={{ fontSize:13 }}>{c.firstName} {c.lastName}</strong>
            <Badge c={STAGE_COLOR[c.stage]||G.purple} bg={(STAGE_COLOR[c.stage]||G.purple)+'22'}>{c.stage}</Badge>
          </div>
          <div style={{ fontSize:11, color:G.textMuted, marginTop:3 }}>{c.service || ''} · {c.source || ''}</div>
          <div style={{ fontSize:11, color:G.textDim, marginTop:2 }}>{c.city || c.address || '—'}</div>
        </div>)}
        {filtered.length === 0 && <div style={{ color:G.textMuted, fontSize:13, padding:16, textAlign:'center' }}>
          {contacts.length === 0
            ? 'No contacts on this account yet. Add contacts in the Contacts screen or import via Migration.'
            : `No contacts in the last ${recency} days. Try a wider window (90d, 1y, or All) — this account has ${contacts.length} total contact${contacts.length===1?'':'s'}.`}
        </div>}
      </div>
    </div>

    <div style={{ display:'flex', gap:14, fontSize:12, color:G.textMuted, flexWrap:'wrap' }}>
      <span style={{ fontWeight:700 }}>Legend:</span>
      {Object.entries(STAGE_COLOR).map(([s,c]) => <span key={s} style={{ display:'flex', alignItems:'center', gap:5 }}>
        <span style={{ width:10, height:10, borderRadius:'50%', background:c }}/>{s}
      </span>)}
    </div>
  </div>;
}
